import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  HandleCleanProjects,
  LoadProjectById,
  LoadProjects,
  LoadResponsibles,
  LoadRoles,
  MainContext,
  LoadDicTypes,
  LoadDicTypesTermin,
  FindDynamicSVGReactElement,
  ImportSVGIconStringForImg,
} from './MainContext';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import SockJsClient from 'react-stomp';
import {
  fetchMeUser,
  fetchMeUserFull,
  getPersonInfoDescriptions,
  getPersonProfileShortInfo,
  getResponsibles,
} from '../../store/slices/personsSlice/personsSlice';
import useSearchApplications from './useSearchApplications';
import { ApplicationsMapSearchType, ApplicationsSearchType } from '../../lib/interfaces/SearchApplicationsBody';
import { Navbar } from '../../components/Navbar/Navbar';
import LocalStorageService from '../../services/LocalStorageService';
import AppRoutes from '../../router/AppRoutes';
import usePermissions from '../../lib/utils/usePermissions';
import AuthService from '../../services/AuthService';
import { cleanProjects, getProjectById, getProjects } from '../../store/slices/projectsSlice/projectsSlice';
import { Button, Form, Modal, Row, Spin } from 'antd';
import { checkIsLoaded, checkIsLoading } from '../../lib/utils/checkLoadingStatus';
import {
  getUnreadNotificationsCount,
  pushNewNotification,
} from '../../store/slices/notificationsSlice/notificationsSlice';
import localStorageService from '../../services/LocalStorageService';
import { getPersonTypesDictionary } from '../../store/slices/dictionariesSlice/dictionariesSlice';
import { getProperties } from '../../store/slices/rootSlice/rootSlice';
import useWindowDimensions from '../../lib/utils/useWindowDimensions';
import { fetchApplicationIcons } from '../../store/slices/applicationsSlice/applicationsSlice';
import { DynamicSVGReactElement } from '../../components/common/DynamicSVGReactElement';
import { collectionSvg } from '../../lib/utils/collectionSvg';
import { collectionImportantSvg } from '../../lib/utils/collectionImportantSvg';
import { getRoles } from '../../store/slices/rolesSlice/rolesSlice';
import {
  getDicTypes,
  getDicTypesTermin,
} from '../../store/slices/administrationDictionariesSlice/administrationDictionariesSlice';
import axios from 'axios';
import { isMobileOnly, isTablet } from 'react-device-detect';
import AgreementDocumentsModal from '../../components/AgreementDocumentsModal/AgreementDocumentsModal';
import { profileRoute } from '../../lib/routes/profile';
import { message } from 'antd/es';

interface IProps {}

export const MainContainer: FC<IProps> = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = !!LocalStorageService.getAccessToken();
  const [hasAccess] = usePermissions();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchType, setSearchType] = useState<ApplicationsSearchType>('ALL');
  const [mapSearchType, setMapSearchType] = useState<ApplicationsMapSearchType>('MAP_ALL');
  const { personProfileShortInfo, meUserFull, meUserFullLoading, personInfoDescriptions, meUser } = useAppSelector(
    (state) => state.persons
  );
  const { personTypesLoading } = useAppSelector((state) => state.dictionaries);
  const { properties, propertiesLoading } = useAppSelector((state) => state.root);
  const { applicationIcons } = useAppSelector((state) => state.applications);

  const [agreementForm] = Form.useForm();
  const [createAgreementModalVisible, setCreateAgreementModalVisible] = useState(false);

  const searchApplicationsStateMy = useSearchApplications({
    searchType: 'MY',
    hasAccess,
  });
  const searchApplicationsStateAll = useSearchApplications({
    searchType: 'ALL',
    hasAccess,
  });
  const searchApplicationsMapStateMy = useSearchApplications({
    searchType: 'MAP_MY',
    hasAccess,
  });
  const searchApplicationsMapStateAll = useSearchApplications({
    searchType: 'MAP_ALL',
    hasAccess,
  });

  const windowDimensions = useWindowDimensions();

  const getSearchApplicationsState = () => {
    const currentTypes: {
      '/applications/list': ApplicationsSearchType;
      '/applications/list/': ApplicationsSearchType;
      '/applications/map': ApplicationsMapSearchType;
      '/applications/map/': ApplicationsMapSearchType;
    } = {
      '/applications/list': searchType,
      '/applications/list/': searchType,
      '/applications/map': mapSearchType,
      '/applications/map/': mapSearchType,
    };
    switch (currentTypes[pathname]) {
      default:
      case 'MY':
        return searchApplicationsStateMy;
      case 'ALL':
        return searchApplicationsStateAll;
      case 'MAP_MY':
        return searchApplicationsMapStateMy;
      case 'MAP_ALL':
        return searchApplicationsMapStateAll;
    }
  };

  const loadResponsibles: LoadResponsibles = ({ projectIds, personStatusIds }) => {
    dispatch(getResponsibles({ projectIds, personStatusIds }));
  };

  const loadProjects: LoadProjects = (searchParams) => {
    return dispatch(getProjects(searchParams || {}));
  };

  const loadProjectById: LoadProjectById = (projectId) => {
    dispatch(getProjectById(projectId));
  };

  const loadDicTypes: LoadDicTypes = () => {
    return dispatch(getDicTypes());
  };

  const loadDicTypesTermin: LoadDicTypesTermin = (dicTypeId) => {
    return dispatch(getDicTypesTermin(dicTypeId));
  };

  const handleCleanProjects: HandleCleanProjects = () => {
    dispatch(cleanProjects());
  };

  const findDynamicSVGReactElement: FindDynamicSVGReactElement = (dicId, dopIconName, typeIco, subjectId, urgent) => {
    const findNameIcon = applicationIcons.find((icon) => icon.dicId === subjectId)?.[typeIco];

    return (
      <DynamicSVGReactElement
        name={findNameIcon || dopIconName}
        dopIcon={dopIconName}
        dicId={dicId}
        urgent={urgent}
      />
    );
  };

  const importSVGIconStringForImg: ImportSVGIconStringForImg = (dicId, dopIconName, typeIco, subjectId, urgent) => {
    const findIcon = applicationIcons.find((icon) => icon.dicId === subjectId);

    if (findIcon) {
      if (urgent) {
        return collectionImportantSvg[dicId][findIcon[typeIco]];
      } else {
        return collectionSvg[dicId][findIcon[typeIco]];
      }
    } else {
      if (urgent) {
        return collectionImportantSvg[dicId][dopIconName] || '';
      } else {
        return collectionSvg[dicId][dopIconName] || '';
      }
    }
  };

  const loadRoles: LoadRoles = (searchParams) => {
    return dispatch(getRoles(searchParams));
  };

  const handleAgreementModalOk = () => {
    const body = {
      ...personProfileShortInfo,
      agreeDocuments: {
        document_terms_of_use: properties?.agree_documents?.terms_of_use,
        document_privacy_policy: properties?.agree_documents?.privacy_policy,
        document_personal_data: properties?.agree_documents?.personal_data,
      },
    };
    axios
      .patch(profileRoute(), body)
      .then(() => {
        message.success('Подтверждение прошло успешно');
      })
      .catch(() => {
        message.error('Произошла ошибка. Пожалуйста, попробуйте позднее');
      });
    setCreateAgreementModalVisible(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getPersonProfileShortInfo());
    }
  }, []);

  // 1. Загрузка текущего пользователя
  useEffect(() => {
    /* Странно конечно, получать id пользователя, а после запрашивать по нему данные.
    Но, информации в me не хватает также currentUser содержит только personId, а нужен userId */
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      dispatch(fetchMeUser()).then(({ payload }) => {
        if (!payload?.approvalAgreeDocuments) {
          setCreateAgreementModalVisible(true);
        } else {
          setCreateAgreementModalVisible(false);
        }
        if (payload?.id) {
          // Запрашиваем информацию о пользователе для проверки необходимости принудительной смены пароля
          dispatch(getPersonInfoDescriptions({ userId: currentUser.user_id }));
        } else {
          LocalStorageService.clearToken();
          navigate('/login');
        }
      });
    }
  }, [dispatch]);

  // 2. Анализируем короткую информацию о пользователе
  useEffect(() => {
    // Если есть данные о пользователе и короткая информация о нем
    if (meUser?.id && personInfoDescriptions?.id) {
      // Если короткая информация говорит об отсутствии необходимости принудительной смены пароля
      if (personInfoDescriptions?.dsc !== 'reset password') {
        dispatch(fetchMeUserFull({ personId: meUser.id }));
        // Если короткая информация говорит о необходимости принудительной смены пароля
      } else if (personInfoDescriptions?.dsc === 'reset password') {
        navigate('/force_password_change');
      } else {
        navigate('/login');
      }
    }
  }, [personInfoDescriptions, meUser?.id]);

  useEffect(() => {
    !checkIsLoaded(personTypesLoading) && dispatch(getPersonTypesDictionary());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getProperties({ tenantId: 1 }));
    isAuthenticated && dispatch(fetchApplicationIcons({ dicType: 'app_subject' }));
  }, [dispatch]);

  useEffect(() => {
    if (meUserFull?.userId) {
      // dispatch(getNotifications({page: 0, size: 20, userId: meUserFull.userId, searchUnread: true}))
      dispatch(getUnreadNotificationsCount());
    }
  }, [meUserFull, dispatch]);

  const [modalConfirmationEmailVisible, setModalConfirmationEmailVisible] = useState(false);

  // Для проверки подтверждения почты
  useEffect(() => {
    const isPageFetch = ['/applications/map'];
    const isModalClosed = LocalStorageService.getObject('modalConfirmationEmailClosed') === 'true';

    if (
      meUserFull?.userId &&
      meUserFull.emailConfirmation === false &&
      isPageFetch.includes(pathname) &&
      !isModalClosed
    ) {
      setModalConfirmationEmailVisible(true);
    }
  }, [meUserFull, pathname]);

  const onModalConfirmationEmailClose = () => {
    LocalStorageService.setObject('modalConfirmationEmailClosed', 'true');
    setModalConfirmationEmailVisible(false);
  };

  // Если требуется принудительная смена пароля
  if (personInfoDescriptions?.dsc === 'reset password') {
    return <AppRoutes isAuth={false} isResetPassword />;
  }

  const renderNavBar = () => {
    if (isMobileOnly || isTablet) {
      return <></>;
    } else {
      if (isAuthenticated) {
        return <Navbar />;
      } else {
        return <></>;
      }
    }
  };

  // Если требуется принудительная смена пароля
  if (personInfoDescriptions?.dsc === 'reset password') {
    return (
      <>
        <AppRoutes isAuth={false} isResetPassword />
        <AgreementDocumentsModal
          isOpened={createAgreementModalVisible}
          handleOk={handleAgreementModalOk}
          form={agreementForm}
        />
      </>
    );
  }

  if (isAuthenticated) {
    return (
      <Spin spinning={checkIsLoading(meUserFullLoading, personTypesLoading)}>
        {checkIsLoaded(meUserFullLoading, personTypesLoading, propertiesLoading) && (
          <MainContext.Provider
            value={{
              hasAccess,
              searchType,
              mapSearchType,
              setSearchType,
              setMapSearchType,
              searchApplicationsState: getSearchApplicationsState(),
              loadResponsibles,
              loadProjects,
              loadProjectById,
              loadDicTypes,
              loadDicTypesTermin,
              handleCleanProjects,
              loadRoles,
              windowDimensions,
              findDynamicSVGReactElement,
              importSVGIconStringForImg,
              searchApplicationsMapStateAll,
            }}
          >
            <SockJsClient
              url={`https://api.sdn104.iflex.ru/notifications/notifications/?access_token=${localStorageService.getAccessToken()}`}
              onMessage={(msg) => {
                dispatch(pushNewNotification(msg));
              }}
              topics={[`/user/${meUserFull?.userId}/push`]}
            />
            {renderNavBar()}
            <AppRoutes isAuth={true} />
            <Modal
              centered
              width={410}
              open={modalConfirmationEmailVisible}
              onOk={onModalConfirmationEmailClose}
              onCancel={onModalConfirmationEmailClose}
              closable={false}
              footer={null}
              maskClosable={false}
            >
              <Row className="font-roboto">
                Уважаемый пользователь!
                <br />
                Ваша почта не подтверждена.
              </Row>
              <Row className="font-roboto mt19" wrap={false}>
                <Button
                  style={{ maxWidth: '123px', minWidth: '123px' }}
                  className="mr16"
                  onClick={onModalConfirmationEmailClose}
                >
                  Пропустить
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    onModalConfirmationEmailClose();
                    navigate('/profile');
                  }}
                >
                  Подтвердить в профиле
                </Button>
              </Row>
            </Modal>
            <AgreementDocumentsModal
              isOpened={createAgreementModalVisible}
              handleOk={handleAgreementModalOk}
              form={agreementForm}
            />
          </MainContext.Provider>
        )}
      </Spin>
    );
  }

  return <AppRoutes isAuth={false} />;
};
